import { useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";
import { useDashboardOverview, useTreesList } from "@/hooks";
import TreeCard from "./components/TreeCard";
import TreeTable from "./components/TreeTable";
import TreeFilters from "./components/TreeFilters";
import TreeQualityPanel from "./components/TreeQualityPanel";
import PageHeader from "@/components/shared/PageHeader";
import KpiCard from "@/components/shared/KpiCard";
import EmptyState from "@/components/shared/EmptyState";
import { markCameFromCta } from "../coletor/fromCta";

export default function CTA() {
  const navigate = useNavigate();
  // No celular a tabela exige rolagem lateral: comeca em cartoes.
  const [viewMode, setViewMode] = useState<"cards" | "table">(() =>
    typeof window !== "undefined" && window.matchMedia?.("(max-width: 767px)").matches ? "cards" : "table",
  );
  const [filters, setFilters] = useState<Record<string, string>>({});
  const { items: individuos, loading, error } = useTreesList();
  const { data: overview } = useDashboardOverview();

  // Indicadores so com dado real; sem fonte, o indicador nao aparece.
  const count = (labels: string[]) =>
    overview ? overview.riskDistribution.filter((r) => labels.includes(r.label)).reduce((a, r) => a + r.count, 0) : null;
  const comVistoriaValidada = overview ? overview.totalTrees - overview.treesWithoutValidatedInspection : null;
  const fmt = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString("pt-BR"));

  const filtered = useMemo(() => {
    return individuos.filter((ind) => {
      if (filters.search) {
        const s = filters.search;
        if (
          !ind.codigoCta.toLowerCase().includes(s) &&
          !ind.especie.toLowerCase().includes(s) &&
          !ind.nomePopular.toLowerCase().includes(s) &&
          !ind.nomeCientifico.toLowerCase().includes(s) &&
          !ind.logradouro.toLowerCase().includes(s) &&
          !ind.bairro.toLowerCase().includes(s)
        ) return false;
      }
      if (filters.tipo && ind.tipo !== filters.tipo) return false;
      if (filters.fito && ind.estadoFitossanitario !== filters.fito) return false;
      if (filters.risco && ind.classeRisco !== filters.risco) return false;
      if (filters.recomendacao && ind.recomendacao !== filters.recomendacao) return false;
      if (filters.statusRevisao && ind.statusRevisao !== filters.statusRevisao) return false;
      return true;
    });
  }, [filters, individuos]);

  const statusOptions = useMemo(
    () => Array.from(new Set(individuos.map((i) => i.statusRevisao).filter(Boolean))).sort(),
    [individuos]
  );

  // CSV da lista filtrada; BOM + ";" para o Excel em portugues abrir com acentos e colunas certas.
  const exportCsv = () => {
    const header = ["Codigo CTA", "Especie", "Nome cientifico", "Tipo", "Projeto", "Logradouro", "Bairro",
      "Municipio", "Coordenada", "Classe de risco", "Ultima vistoria", "Status de revisao"];
    const cell = (v: string) => `"${(v ?? "").replace(/"/g, '""')}"`;
    const rows = filtered.map((i) => [i.codigoCta, i.especie, i.nomeCientifico, i.tipo, i.projeto, i.logradouro,
      i.bairro, i.municipio, i.coordenada, i.classeRisco, i.ultimaVistoria, i.statusRevisao].map(cell).join(";"));
    const blob = new Blob(["﻿" + [header.map(cell).join(";"), ...rows].join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cta_exemplares_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="CTA Arbóreo"
        subtitle="Cadastro Técnico Arbóreo com indivíduos georreferenciados, fichas técnicas, risco, vistorias e histórico."
      >
        <button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          className="btn-outline text-xs px-3 py-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          title="Exporta a lista filtrada em CSV"
        >
          <i className="ri-download-2-line"></i>
          Exportar
        </button>
        <button onClick={() => { markCameFromCta(); navigate("/coletor"); }} className="btn-outline text-xs px-3 py-2 cursor-pointer">
          <i className="ri-smartphone-line"></i>
          Coletor de campo
        </button>
        <button onClick={() => navigate("/manual/cta")} className="btn-outline text-xs px-3 py-2 cursor-pointer" title="Como usar o CTA, passo a passo">
          <i className="ri-book-open-line"></i>
          Manual
        </button>
        <button onClick={() => navigate("/mapa")} className="btn-outline text-xs px-3 py-2 cursor-pointer">
          <i className="ri-map-2-line"></i>
          Abrir mapa
        </button>
        <button
          onClick={() => navigate("/cta/novo")}
          className="btn-primary text-xs px-3 py-2 cursor-pointer"
        >
          <i className="ri-add-line"></i>
          Adicionar CTA
        </button>
      </PageHeader>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4">
        <KpiCard label="Exemplares cadastrados" value={fmt(overview?.totalTrees)} icon="ri-plant-line" variant="success" />
        <KpiCard label="Com vistoria validada" value={fmt(comVistoriaValidada)} icon="ri-check-double-line" variant="success" />
        <KpiCard label="Vistorias em revisão" value={fmt(overview?.pendingReview)} icon="ri-edit-line" variant="warning" />
        <KpiCard label="Risco crítico" value={fmt(count(["CRITICO"]))} icon="ri-alert-line" variant="danger" />
        <KpiCard label="Risco alto" value={fmt(count(["ALTO"]))} icon="ri-error-warning-line" variant="warning" />
        <KpiCard label="Espécies em uso" value={fmt(overview?.totalSpeciesInUse)} icon="ri-leaf-line" variant="info" />
      </div>

      {/* Filters */}
      <TreeFilters onFilter={setFilters} statusOptions={statusOptions} />

      {/* View Toggle + Results */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground-500">
          {filtered.length} {filtered.length === 1 ? "indivíduo" : "indivíduos"} encontrado{filtered.length !== 1 ? "s" : ""}
          {Object.keys(filters).length > 0 && " (com filtros aplicados)"}
        </p>
        <div className="flex items-center bg-white rounded-lg border border-background-200/70 p-0.5">
          <button
            onClick={() => setViewMode("table")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
              viewMode === "table" ? "bg-primary-50 text-primary-700" : "text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <i className="ri-table-line mr-1"></i>Tabela
          </button>
          <button
            onClick={() => setViewMode("cards")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
              viewMode === "cards" ? "bg-primary-50 text-primary-700" : "text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <i className="ri-grid-line mr-1"></i>Cards
          </button>
        </div>
      </div>

      {/* Main Content */}
      {filtered.length > 0 ? (
        viewMode === "table" ? (
          <TreeTable individuos={filtered} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((ind) => (
              <TreeCard key={ind.id} individuo={ind} />
            ))}
          </div>
        )
      ) : (
        <EmptyState
          icon="ri-plant-line"
          title={
            loading ? "Carregando exemplares…" : error ? "Não foi possível carregar o cadastro" : individuos.length === 0 ? "Nenhum exemplar cadastrado" : "Nenhum exemplar encontrado"
          }
          description={
            loading ? "" : error ? error : individuos.length === 0
              ? "A base começa vazia. Use “Adicionar CTA” para cadastrar o primeiro exemplar pelo mapa."
              : "Ajuste os filtros para ver mais resultados."
          }
        />
      )}

      {/* Quality */}
      <TreeQualityPanel individuos={individuos} comVistoriaValidada={comVistoriaValidada} />
    </div>
  );
}